import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound, CheckCircle2, XCircle } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

export default function AdminResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [info, setInfo] = useState(null);

  useEffect(() => { api.get("/church-info").then((r) => setInfo(r.data)).catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!token) { setError("Lien invalide"); return; }
    if (password.length < 6) { setError("Le mot de passe doit contenir au moins 6 caractères"); return; }
    if (password !== confirm) { setError("Les deux mots de passe ne correspondent pas"); return; }

    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      toast.success("Mot de passe mis à jour");
      setTimeout(() => navigate("/admin/login"), 2500);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Erreur");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden bg-deepblue-900" data-testid="reset-password-page">
      <div className="absolute inset-0 bg-gradient-to-br from-deepblue-900 via-deepblue-900/95 to-deepblue-800" />
      <div className="absolute inset-0 grain opacity-60" />

      <div className="relative w-full max-w-md mx-4">
        <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8 shadow-2xl">
          {!token ? (
            <div className="text-center py-4">
              <XCircle className="w-16 h-16 mx-auto text-red-400 mb-4" />
              <h1 className="font-serif text-2xl text-white">Lien invalide</h1>
              <p className="mt-3 text-white/70 text-sm">Ce lien de réinitialisation n'est pas valide.</p>
              <Link to="/admin/forgot-password">
                <Button className="mt-6 w-full bg-gold hover:bg-gold-hover text-white rounded-full">
                  Demander un nouveau lien
                </Button>
              </Link>
            </div>
          ) : done ? (
            <div className="text-center py-4">
              <CheckCircle2 className="w-16 h-16 mx-auto text-emerald-400 mb-4" />
              <h1 className="font-serif text-2xl text-white">Mot de passe mis à jour ✓</h1>
              <p className="mt-3 text-white/70 text-sm">Vous allez être redirigé vers la connexion…</p>
            </div>
          ) : (
            <>
              <div className="text-center mb-8">
                {info?.logo_url ? (
                  <img src={info.logo_url} alt="Logo" className="w-20 h-20 mx-auto rounded-full object-cover ring-4 ring-gold/40 bg-white mb-4" />
                ) : (
                  <div className="w-16 h-16 mx-auto rounded-full bg-gold/20 flex items-center justify-center border border-gold/40 mb-4">
                    <KeyRound className="w-7 h-7 text-gold" />
                  </div>
                )}
                <h1 className="font-serif text-3xl text-white">Nouveau mot de passe</h1>
                <p className="mt-2 text-white/60 text-sm">Choisissez un mot de passe sécurisé (min. 6 caractères).</p>
              </div>

              <form onSubmit={submit} className="space-y-5" data-testid="reset-form">
                <div>
                  <Label className="text-white/80 text-xs uppercase tracking-widest">Nouveau mot de passe</Label>
                  <Input type="password" required autoFocus value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="mt-2 bg-white/95 border-transparent text-deepblue-900"
                    data-testid="reset-password" />
                </div>
                <div>
                  <Label className="text-white/80 text-xs uppercase tracking-widest">Confirmer le mot de passe</Label>
                  <Input type="password" required value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    className="mt-2 bg-white/95 border-transparent text-deepblue-900"
                    data-testid="reset-confirm" />
                </div>

                {error && (
                  <div className="rounded-lg bg-red-500/20 border border-red-500/40 text-red-100 text-sm px-4 py-3">
                    {error}
                  </div>
                )}

                <Button type="submit" disabled={submitting}
                  className="w-full bg-gold hover:bg-gold-hover text-white rounded-full py-6"
                  data-testid="reset-submit">
                  <KeyRound className="w-4 h-4 mr-2" />
                  {submitting ? "Mise à jour…" : "Réinitialiser le mot de passe"}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
